<template>
  <div class="page page__product-detail">
    <product-detail-header :product="product"></product-detail-header>

    <favorites-drawer></favorites-drawer>

    <el-container direction="vertical" class="page--container">
      <section class="page--section" v-if="product.overview" id="overview">
        <h2 class="section__title">{{ $t('productDetailOverviewHeading') }}</h2>
        <product-overview
          :mainImage="product.mainImage && product.mainImage[0]"
          :text="product.overview"
          :tagLine="product.tagLine"
        ></product-overview>
      </section>

      <section
        class="page--section"
        v-if="product.specifications && product.specifications.length"
        id="specifications"
      >
        <h2 class="section__title">
          {{ $t('productDetailSpecificationsHeading') }}
        </h2>
        <product-specifications
          :specifications="product.specifications"
        ></product-specifications>
      </section>

      <section
        class="page--section"
        v-if="product.features && product.features.length"
        id="features"
      >
        <h2 class="section__title">{{ $t('productDetailFeaturesHeading') }}</h2>
        <product-features :features="product.features"></product-features>
      </section>

      <section
        class="page--section"
        v-for="collection in product.mediaCollections"
        :key="collection.id"
        :id="'collection-' + collection.id"
      >
        <media-collections
          :collection="collection"
          :product-id="product.id"
        ></media-collections>
      </section>
    </el-container>

    <footer-main />

    <product-detail-sub-menu :product="product"></product-detail-sub-menu>
  </div>
</template>

<script>
import i18n from '@/configured/vue-i18n';
import products from '@/components/products';
import ProductDetailHeader from '@/components/products/ProductDetailHeader.vue';
import ProductOverview from '@/components/products/ProductOverview.vue';
import ProductSpecifications from '@/components/products/ProductSpecifications.vue';
import ProductFeatures from '@/components/products/ProductFeatures.vue';
import MediaCollections from '@/components/MediaCollections.vue';
import ProductDetailSubMenu from '@/components/products/ProductDetailSubMenu.vue';
import FavoritesDrawer from '@/components/FavoritesDrawer.vue';
import FooterMain from '@/components/footer/FooterMain.vue';
import { mapState } from 'vuex';
import rotuer from '@/router';

export default {
  name: 'ProductDetail',
  props: ['id'],
  computed: {
    ...mapState(products.store.namespace, [
      'products',
      'hotLoadedProductDetails',
    ]),
    product() {
      const productId = this.$route.params.id;
      const currentLanguage =
        i18n.i18n.locale || this.$store.state.i18n.language || 'en';
      const hotLoadedForLanguage = this.hotLoadedProductDetails
        ? this.hotLoadedProductDetails[currentLanguage]
        : null;
      
      // Prefer hot-loaded details scoped to current language.
      if (hotLoadedForLanguage && hotLoadedForLanguage[productId]) {
        return hotLoadedForLanguage[productId];
      }
      
      // Fallback to cached products if hotLoadedProductDetails not available
      if (this.products) {
        let productInfo = this.$store.state.products.products.filter(
          (product) => {
            return Number(product.id) === Number(productId);
          }
        );
        if (productInfo.length && productInfo[0].hasDetail) {
          return this.$store.getters[
            `${products.store.namespace}/getProductById`
          ](parseInt(productId));
        }
      }
      
      return null;
    },
  },
  methods: {
    setPageTitle,
  },
  beforeMount() {
    this.setPageTitle();
  },
  updated() {
    this.setPageTitle();
  },
  components: {
    'product-detail-header': ProductDetailHeader,
    'product-overview': ProductOverview,
    'product-specifications': ProductSpecifications,
    'product-features': ProductFeatures,
    'media-collections': MediaCollections,
    'product-detail-sub-menu': ProductDetailSubMenu,
    'favorites-drawer': FavoritesDrawer,
    'footer-main': FooterMain,
  },
  beforeRouteUpdate(to, from, next) {
    const currentLanguage =
      i18n.i18n.locale || this.$store.state.i18n.language || 'en';
    const hotLoadedForLanguage =
      this.$store.state.products.hotLoadedProductDetails &&
      this.$store.state.products.hotLoadedProductDetails[currentLanguage];

    // if the product we are navigating to is not in products, update individualProductData with new id
    let productWithDetailsInProducts = false;
    if (this.products) {
      let productInfo = this.$store.state.products.products.filter(
        (product) => {
          return Number(product.id) === Number(to.params.id);
        }
      );
      if (productInfo.length) {
        productWithDetailsInProducts = productInfo[0].hasDetail;
      }
    }
    if (!hotLoadedForLanguage || !hotLoadedForLanguage[to.params.id]) {
      this.$store
        .dispatch(`${products.store.namespace}/loadIndividualProductDataById`, {
          id: to.params.id,
        })
        .then(() => {
          next();
        });
    } else if (productWithDetailsInProducts) {
      next();
    } else {
      next();
    }
  },
};

//////////

function setPageTitle() {
  const translationKey = 'pageTitleProductDetail';
  const pageTitle = i18n.i18n.t(translationKey);
  const divider = i18n.i18n.t(`pageTitleDivider`);
  const siteName = i18n.i18n.t('siteName');

  if (this.product && this.product.title) {
    document.title = this.product.title + divider + siteName;
  } else if (pageTitle === translationKey) {
    document.title = siteName;
  } else {
    document.title = pageTitle + divider + siteName;
  }
}
</script>

<style lang="scss" scoped>
@import 'element-theme-chalk/src/mixins/mixins.scss';

.page {
  padding: 0 0 40px;

  @include res(md) {
    padding: 0 0 40px;
  }
}
</style>
